import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { useSchemas, useInvalidateCache } from '../../hooks/useDbDictionary.js';
import { useAuth } from '../../hooks/useAuth.js';
import { ErrorState } from '../../components/feedback/ErrorState/index.js';
import { KpiTableSkeleton } from '../../components/feedback/KpiTableSkeleton/index.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DB辞書スキーマ一覧ページ。全スキーマのテーブル数・レビュー進捗を表示
 * @graph-connects useSchemas [calls] スキーマ一覧フック呼び出し
 */
export const Route = createFileRoute('/db-dictionary/')({
  component: DbDictionarySchemaListPage,
});

/**
 * DB種別に応じたバッジバリアントを取得
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business DB種別（MySQL/PostgreSQL/MongoDB）に応じたBadgeコンポーネントのバリアント色を決定
 * @graph-connects none
 */
function getDbTypeBadgeVariant(dbType: string): 'info' | 'success' | 'warning' | 'default' {
  switch (dbType) {
    case 'MySQL':
      return 'info';
    case 'PostgreSQL':
      return 'success';
    case 'MongoDB':
      return 'warning';
    default:
      return 'default';
  }
}

/**
 * 進捗率に応じた色クラスを取得
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business レビュー進捗率（0-100%）に応じたプログレスバーの色クラス（success/warning/error）を決定
 * @graph-connects none
 */
function getProgressColor(pct: number): string {
  if (pct >= 80) return 'bg-[var(--color-success)]';
  if (pct >= 50) return 'bg-[var(--color-warning)]';
  return 'bg-[var(--color-error)]';
}

/**
 * スキーマ一覧ページ
 * @graph-connects none
 */
function DbDictionarySchemaListPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canEdit = can('EDIT_DB_DICTIONARY');
  const { data: schemas, isLoading, isError, refetch } = useSchemas();
  const invalidateCache = useInvalidateCache();
  const [cacheMessage, setCacheMessage] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('dbDictionary.title')}
          </h1>
          <p className="mt-1 text-[var(--color-text-secondary)]">{t('dbDictionary.description')}</p>
        </div>
        <KpiTableSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('dbDictionary.title')}
          </h1>
          <p className="mt-1 text-[var(--color-text-secondary)]">{t('dbDictionary.description')}</p>
        </div>
        <ErrorState
          onRetry={() => {
            void refetch();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ページヘッダー */}
      <div className="animate-content-reveal">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('dbDictionary.title')}
          </h1>
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              loading={invalidateCache.isPending}
              onClick={() => {
                setCacheMessage(null);
                invalidateCache.mutate(undefined, {
                  onSuccess: (data) => {
                    setCacheMessage(
                      t('dbDictionary.cache.invalidated', { count: data.deletedKeys }),
                    );
                    setTimeout(() => {
                      setCacheMessage(null);
                    }, 3000);
                  },
                  onError: () => {
                    setCacheMessage(t('dbDictionary.cache.failed'));
                    setTimeout(() => {
                      setCacheMessage(null);
                    }, 3000);
                  },
                });
              }}
            >
              {invalidateCache.isPending
                ? t('dbDictionary.cache.invalidating')
                : t('dbDictionary.cache.invalidate')}
            </Button>
          )}
        </div>
        <p className="mt-1 text-[var(--color-text-secondary)]">{t('dbDictionary.description')}</p>
        {cacheMessage && (
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">{cacheMessage}</p>
        )}
      </div>

      {/* スキーマカードグリッド */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {schemas?.map((schema) => {
          const checkedPct =
            schema.tableCount > 0 ? Math.round((schema.checkedCount / schema.tableCount) * 100) : 0;

          return (
            <Link
              key={schema.name}
              to="/db-dictionary/$schema"
              params={{ schema: schema.name }}
              className="block"
            >
              <Card hoverable className="animate-content-reveal cursor-pointer h-full">
                <CardContent className="mt-0">
                  {/* スキーマ名とDB種別 */}
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <h3 className="text-lg font-semibold text-[var(--color-text-primary)] truncate">
                      {schema.name}
                    </h3>
                    <Badge variant={getDbTypeBadgeVariant(schema.dbType)} size="sm">
                      {schema.dbType}
                    </Badge>
                  </div>

                  {/* 統計情報 */}
                  <div className="grid grid-cols-3 gap-3 text-sm mb-4">
                    <div>
                      <p className="text-[var(--color-text-muted)]">
                        {t('dbDictionary.stats.tableCount')}
                      </p>
                      <p className="text-[var(--color-text-primary)] font-medium">
                        {schema.tableCount}
                      </p>
                    </div>
                    <div>
                      <p className="text-[var(--color-text-muted)]">
                        {t('dbDictionary.stats.checkedCount')}
                      </p>
                      <p className="text-[var(--color-text-primary)] font-medium">
                        {schema.checkedCount}
                      </p>
                    </div>
                    <div>
                      <p className="text-[var(--color-text-muted)]">
                        {t('dbDictionary.stats.deadTableCount')}
                      </p>
                      <p className="text-[var(--color-text-primary)] font-medium">
                        {schema.deadTableCount}
                      </p>
                    </div>
                  </div>

                  {/* 進捗バー */}
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-[var(--color-text-muted)]">
                        {t('dbDictionary.stats.progress')}
                      </span>
                      <span className="text-[var(--color-text-secondary)] font-medium">
                        {checkedPct}%
                      </span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-[var(--color-bg-elevated)]">
                      <div
                        className={`h-2 rounded-full transition-all ${getProgressColor(checkedPct)}`}
                        style={{ width: `${String(checkedPct)}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* データなし */}
      {schemas && schemas.length === 0 && (
        <Card className="animate-content-reveal">
          <CardContent>
            <div className="text-center py-8 text-[var(--color-text-muted)]">
              {t('status.noData')}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
